import type { UiOperator } from "@tsmono/inspect-components/columnFilter";

import {
  kScoreTypeCategorical,
  kScoreTypeNumeric,
  kScoreTypePassFail,
} from "../../../constants";
import { perScorerFieldKey } from "../../shared/samples-grid/columns";
import { EvalDescriptor } from "../descriptor/types";

import { bannedShortScoreNames, builtinFilterVariables } from "./filters";

export type FilterVarKind = "string" | "number";

export interface FilterVarMapping {
  /** Filtrex variable name. */
  variable: string;
  kind: FilterVarKind;
  /** If present, prefer this filtrex function for `contains`-style
   *  column-filter predicates — matches the case-insensitive semantics of
   *  the existing `_contains` helpers. */
  containsFn?: string;
}

export interface SampleFilterSpecRegistry {
  /** Lookup by samples-grid column id → filtrex variable. */
  byColId: Map<string, FilterVarMapping>;
  /** Lookup by filtrex variable name → samples-grid column id. */
  byVariable: Map<string, string>;
}

// `sampleId` is intentionally absent: `sample.id` can be either number
// or string, but the column's `valueGetter` stringifies it for filtering
// and filtrex's `==` is strict. A sync would emit `id == "1"`, which
// won't match a numeric `id = 1`. The column UI and the text filter
// each work on their own; they just don't round-trip through each
// other for IDs.
const STATIC_ENTRIES: Array<[string, FilterVarMapping]> = [
  ["sampleUuid", { variable: "uuid", kind: "string" }],
  ["epoch", { variable: "epoch", kind: "number" }],
  [
    "input",
    { variable: "input", kind: "string", containsFn: "input_contains" },
  ],
  [
    "target",
    { variable: "target", kind: "string", containsFn: "target_contains" },
  ],
  [
    "answer",
    { variable: "answer", kind: "string", containsFn: "answer_contains" },
  ],
  ["tokens", { variable: "tokens", kind: "number" }],
  ["duration", { variable: "duration", kind: "number" }],
  [
    "error",
    { variable: "error", kind: "string", containsFn: "error_contains" },
  ],
  ["limit", { variable: "limit", kind: "string" }],
  ["retries", { variable: "retries", kind: "number" }],
  ["fallbacks", { variable: "fallbacks", kind: "number" }],
];

/** Build the column↔filtrex-variable registry. Score columns are added
 *  dynamically from `evalDescriptor.scores` so the variable name matches
 *  the same short/qualified rule used by `scoreVariables` in filters.ts. */
export const buildSampleFilterSpecRegistry = (
  evalDescriptor: EvalDescriptor | undefined
): SampleFilterSpecRegistry => {
  const entries: Array<[string, FilterVarMapping]> = [...STATIC_ENTRIES];

  if (evalDescriptor) {
    const banned = bannedShortScoreNames(evalDescriptor.scores);
    for (const { name, scorer } of evalDescriptor.scores) {
      const colId = perScorerFieldKey({ name, scorer });
      // A top-level scorer named after a built-in variable has no qualified
      // fallback — leave the column unsynced rather than emit a variable
      // that evaluates to the built-in.
      if (name === scorer && builtinFilterVariables.has(name)) {
        continue;
      }
      const variable =
        name === scorer || !banned.has(name) ? name : `${scorer}.${name}`;
      const scoreType = evalDescriptor.scoreDescriptor({
        name,
        scorer,
      })?.scoreType;
      // Only sync score column filters where the column's text/number
      // filter operates on values that match the runtime filtrex
      // variable type. Booleans (filtrex sees `true`/`false`, the
      // column's text filter sees the string `"true"`) and complex
      // types (object/list/other) would generate expressions that
      // never match the underlying values.
      let kind: FilterVarKind;
      if (scoreType === kScoreTypeNumeric) {
        kind = "number";
      } else if (
        scoreType === kScoreTypeCategorical ||
        scoreType === kScoreTypePassFail
      ) {
        kind = "string";
      } else {
        continue;
      }
      entries.push([colId, { variable, kind }]);
    }
  }

  const byColId = new Map(entries);
  const byVariable = new Map(entries.map(([colId, m]) => [m.variable, colId]));
  return { byColId, byVariable };
};

/**
 * Operator choices offered on the samples tab, narrowed to what round-trips
 * through the filtrex bridge: no `in`/`not in` (the recognizer has no filtrex
 * `in` mapping) and no `not between` (no filtrex form existed on main).
 */
export const samplesOperatorsForKind = (kind: FilterVarKind): UiOperator[] =>
  kind === "number"
    ? ["=", "!=", "<", "<=", ">", ">=", "between", "is blank", "is not blank"]
    : [
        "contains",
        "does not contain",
        "starts with",
        "ends with",
        "=",
        "!=",
        "is blank",
        "is not blank",
      ];
